import { useEffect } from 'react';

interface CancelPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  planName: string;
  expiryDate: string;
}

export function CancelPlanModal({ isOpen, onClose, onConfirm, planName, expiryDate }: CancelPlanModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-in fade-in duration-300" 
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="relative bg-white rounded-[20px] shadow-2xl w-full max-w-[380px] overflow-hidden animate-in fade-in zoom-in duration-300">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <div className="w-8" />
            <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] text-center flex-1">
              Cancel Subscription
            </h2>
            <button 
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f4f4f4] transition-colors"
            >
              <span className="material-icons text-[#3a3335] text-[20px]">close</span>
            </button>
          </div>

          <div className="h-[1px] bg-[#ede8e9] w-full mb-6" />

          {/* Icon/Warning Area */}
          <div className="flex justify-center mb-6">
            <div className="w-[60px] h-[60px] rounded-full bg-[#8e2a2f]/[0.08] flex items-center justify-center text-[#8e2a2f]">
              <span className="material-icons text-[32px]">sentiment_very_dissatisfied</span>
            </div>
          </div>

          {/* Message */}
          <div className="text-center mb-8">
            <h3 className="font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[16px] mb-2 tracking-[-0.1px]">
              Are you sure?
            </h3>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[22px] tracking-[-0.1px]">
              We're sad to see you go! If you cancel your <span className="font-bold text-[#3a3335]">{planName}</span>, you will still have access to your benefits until <span className="font-bold text-[#3a3335]">{expiryDate}</span>.
            </p>
          </div>

          <div className="h-[1px] bg-[#ede8e9] w-full mb-6" />

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 h-[48px] bg-[#fcfaf7] rounded-[12px] font-['DM_Sans',sans-serif] font-bold text-[#3a3335] text-[14px] hover:bg-[#f2eee2] transition-all active:scale-[0.98] border border-[#eee]"
            >
              Keep Plan
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 h-[48px] bg-[#8e2a2f] rounded-[12px] font-['DM_Sans',sans-serif] font-bold text-white text-[14px] hover:bg-[#7a2228] transition-all active:scale-[0.98]"
            >
              Yes, Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
